import axios, { getData } from "../../../axios";
import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import {
  decodeCargoTemplate,
  editCargoTemplateOutputT,
  editCargoTemplateInputT,
  postCargoTemplateT,
  CargoTemplateData,
} from "./types";
import { omit } from "lodash";
import {
  apiCargoTemplateToFormData,
  defaultFormData,
  formDataStopToApiStop,
} from "./helpers";
import { notifyL } from "../../notifications";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { AppDispatch, GetState } from "../../../redux-store";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { draftLinesCleared } from "../../data/draft-invoice-lines";
import { loadSchedule } from "../../data/schedules";
import { CargoFormData } from "../cargo-dialog/types";
import { loadAndUseClientCargoTemplate } from "../../data/client-order-templates";
import { DateTime } from "luxon";
import { apiStopToFormStop } from "../cargo-dialog/helpers";
import {
  ERR_OUTDATED_CARGO_REVISION,
  convertStopUnitsDecimalToString,
  convertStopUnitsStringToDecimal,
  ERR_DELETED_EXTRA_CO2_TYPES,
} from "dora-shared";
import {
  dropDeletedExtraCo2TypesParam,
  isDeletedExtraCo2TypesError,
} from "../../data/extra-co2-types";
import { isOutdatedRevisionError } from "../outdated-revision";

export type CargoTemplateFromApi = t.TypeOf<typeof editCargoTemplateOutputT>;

const prefix = "app/cargo-template-dialog";

type CargoDialogStatus = "CREATE" | "EDIT" | "CLOSED";

interface CargoDialogState {
  status: CargoDialogStatus;
  initialFormData?: CargoFormData;
  resetFormData?: CargoFormData;
  previousFormData?: CargoFormData;
  isSaving: boolean;
  // The revision the open template is saved against (DORA-441).
  loadedDataRevisionNumber: string | null;
  // Someone else's change to the open template clashes with the user's
  // unsaved edits (DORA-441).
  remoteConflict: boolean;
  // The template open in the dialog, if any.
  editingTemplateId: string | null;
}

const initialState: CargoDialogState = {
  status: "CLOSED",
  isSaving: false,
  loadedDataRevisionNumber: null,
  remoteConflict: false,
  editingTemplateId: null,
};

// The open template now stands at this revision (DORA-441).
export const openTemplateRevisionAdopted = createAction<string>(
  `${prefix}/open-template-revision-adopted`
);
export const openTemplateRemoteConflict = createAction(
  `${prefix}/open-template-remote-conflict`
);

export const cargoTemplateDeleted = createAction<{
  cargoTemplateId: string;
}>(`${prefix}/cargo-template-deleted`);

export const deleteCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (
    data: {
      cargoTemplateId: string;
      scheduleId: string;
    },
    thunkApi
  ) => {
    const { cargoTemplateId, scheduleId } = data;
    await axios.delete<CargoFormData>(
      `/api/cargo-templates/${cargoTemplateId}?scheduleId=${scheduleId}`
    );
    thunkApi.dispatch({
      type: "DELETE_CARGO_TEMPLATE",
      payload: { cargoTemplateId },
    });
    thunkApi.dispatch(cargoTemplateDeleted({ cargoTemplateId }));
    thunkApi.dispatch(loadSchedule(scheduleId));
  }
);

export const loadScheduleCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (
    cargoTemplateId: string,
    thunkAPI
  ): Promise<{
    cargoTemplateId: string;
    data: CargoFormData;
    revision: string;
  }> => {
    try {
      const decoded = await axios
        .get(`/api/cargo-templates/${cargoTemplateId}`)
        .then(getData)
        .then(decodeCargoTemplate);
      return {
        cargoTemplateId,
        data: apiCargoTemplateToFormData(decoded),
        revision: decoded.revision,
      };
    } catch (e) {
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "loadingCargoTemplateFailed",
          type: "error",
        })
      );
      throw e;
    }
  }
);

// Someone else changed the open template: fetch the latest version for the
// dialog to merge into its form, leaving the dialog's state alone (DORA-441).
export const fetchLatestOpenTemplate = createErrorReportingAsyncThunk(
  `${prefix}/fetch-latest-open-template`,
  async (
    cargoTemplateId: string
  ): Promise<{ data: CargoFormData; revision: string }> => {
    const decoded = await axios
      .get(`/api/cargo-templates/${cargoTemplateId}`)
      .then(getData)
      .then(decodeCargoTemplate);
    return {
      data: apiCargoTemplateToFormData(decoded),
      revision: decoded.revision,
    };
  },
  // A background fetch: a failure shouldn't toast over the user's work.
  { skipNotification: true }
);

export const submit = createErrorReportingAsyncThunk(
  `${prefix}/submit`,
  async (
    formData: CargoTemplateData & {
      keepAlive?: boolean;
      routeId?: string;
      scheduleId: string;
      routeTemplateId: string | null;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      dropDeletedExtraCo2Types?: boolean;
    },
    thunkAPI
  ) => {
    const actualFormData = omit(formData, [
      "submitType",
      "keepAlive",
      "dropDeletedExtraCo2Types",
    ]);
    const params = dropDeletedExtraCo2TypesParam(
      formData.dropDeletedExtraCo2Types
    );
    // const cargoId = selectCargoId(thunkAPI.getState());
    const cargoTemplateId = formData.id;
    const scheduleId = formData.scheduleId;
    // const routeId = formData.routeId;
    const body = {
      ...actualFormData,
      minTemperature: formData.minTemperature || null,
      maxTemperature: formData.maxTemperature || null,
      type: formData.type,
      clientId: formData.clientId || null,
      pickupList: formData.pickupList.map(formDataStopToApiStop),
      dropoffList: formData.dropoffList.map(formDataStopToApiStop),
      stopUnits:
        formData.stopUnits.map((su) => ({
          ...su,
          ...convertStopUnitsDecimalToString(su),
        })) || [],
    };
    const saveTemplate = async () => {
      if (cargoTemplateId) {
        // The revision the dialog loaded (DORA-441).
        const revision =
          thunkAPI.getState().app.scheduleCargoTemplateDialog
            .loadedDataRevisionNumber;
        const url = revision
          ? `/api/cargo-templates/${cargoTemplateId}?revisionNumber=${revision}`
          : `/api/cargo-templates/${cargoTemplateId}`;
        const saved = await axios
          .put(url, editCargoTemplateInputT.encode(body), { params })
          .then(getData)
          .then(tPromise.decode(t.type({ revision: t.string })));
        // A dialog kept open (Ctrl+S) saves next against this revision.
        thunkAPI.dispatch(openTemplateRevisionAdopted(saved.revision));
        thunkAPI.dispatch(
          notifyL({
            namespace: "notifications",
            key: "shipmentTemplateSaved",
            type: "success",
          })
        );
        return undefined;
      }
      const invoiceLines = thunkAPI
        .getState()
        .data.draftInvoiceLines.lines.map((l) => ({
          productId: l.productId,
          unitId: l.unitId,
          description: l.description,
          quantity: l.quantity,
          amount: l.amount,
          discountPercentage: l.discountPercentage,
          percentage: l.percentage,
          isFromRoadToll: l.isFromRoadToll,
        }));
      const created = formData.routeTemplateId
        ? await axios.post(
            `/api/schedules/${scheduleId}/route-templates/${formData.routeTemplateId}/cargo-templates`,
            postCargoTemplateT.encode({
              ...body,
              invoiceLines,
            }),
            { params }
          )
        : await axios.post(
            `/api/cargo-templates`,
            postCargoTemplateT.encode({
              ...body,
              invoiceLines,
            }),
            { params: { ...params, scheduleId } }
          );
      thunkAPI.dispatch(draftLinesCleared());
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "shipmentTemplateCreated",
          type: "success",
        })
      );
      return created;
    };
    let data;
    try {
      data = await saveTemplate();
    } catch (err) {
      // Adding lines whose extra CO2 type was deleted is refused until the
      // user agrees to save without them; the dialog asks and resubmits.
      if (isDeletedExtraCo2TypesError(err)) {
        return {
          requestFormData: actualFormData,
          keepAlive: formData.keepAlive,
          submitType: formData.submitType,
          error: ERR_DELETED_EXTRA_CO2_TYPES,
        };
      }
      // Someone else saved the template meanwhile: the dialog handles it.
      if (isOutdatedRevisionError(err)) {
        return {
          requestFormData: actualFormData,
          keepAlive: formData.keepAlive,
          submitType: formData.submitType,
          error: ERR_OUTDATED_CARGO_REVISION,
        };
      }
      throw err;
    }
    return {
      ...(data && {
        id: data?.data.id,
      }),
      requestFormData: actualFormData,
      keepAlive: formData.keepAlive,
      submitType: formData.submitType,
    };
  }
);

export const createNewCargoTemplate =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState().app.scheduleCargoTemplateDialog;
    const assignTeamId = getState().auth.user?.defaultTeamId;
    const newShipmentDateAdjustment =
      getState().data.orgSettings.newShipmentDate;
    const defaultData = defaultFormData({
      assignedTeamId: assignTeamId!,
      newShipmentDateAdjustment,
    });
    const initialFormData = state.previousFormData || defaultData;
    const resetFormData = defaultData;
    return dispatch(
      slice.actions.createNewCargoTemplate({
        initialFormData,
        resetFormData,
      })
    );
  };

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    createNewCargoTemplate: (
      state,
      action: PayloadAction<{
        initialFormData: CargoFormData;
        resetFormData: CargoFormData;
      }>
    ) => {
      state.status = "CREATE";
      state.initialFormData = action.payload.initialFormData;
      state.previousFormData = undefined;
      state.resetFormData = action.payload.resetFormData;
    },
    close: (state, action: PayloadAction<CargoFormData | undefined>) => {
      state.initialFormData =
        state.status === "EDIT" ? undefined : state.initialFormData;
      state.previousFormData =
        state.status === "CREATE" ? action.payload : undefined;
      state.status = "CLOSED";
      state.loadedDataRevisionNumber = null;
      state.remoteConflict = false;
      state.editingTemplateId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submit.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(submit.fulfilled, (state, { payload }) => {
        state.isSaving = false;
        if ("error" in payload) {
          // refused, the dialog stays open and handles it
          return;
        }
        state.status = "CLOSED";
        state.previousFormData = undefined;
        state.initialFormData = undefined;
      })
      .addCase(submit.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(loadScheduleCargoTemplate.fulfilled, (state, action) => {
        state.initialFormData = action.payload.data;
        state.resetFormData = action.payload.data;
        state.status = "EDIT";
        state.loadedDataRevisionNumber = action.payload.revision;
        // A reload of the open template (after Ctrl+S) keeps a clash flagged
        // meanwhile; another template starts without one (DORA-441).
        if (state.editingTemplateId !== action.payload.cargoTemplateId) {
          state.remoteConflict = false;
        }
        state.editingTemplateId = action.payload.cargoTemplateId;
      })
      .addCase(openTemplateRevisionAdopted, (state, action) => {
        state.loadedDataRevisionNumber = action.payload;
      })
      .addCase(openTemplateRemoteConflict, (state) => {
        state.remoteConflict = true;
      })
      .addCase(deleteCargoTemplate.fulfilled, (state) => {
        state.status = "CLOSED";
        state.initialFormData = undefined;
        state.previousFormData = undefined;
        state.resetFormData = undefined;
      })
      .addCase(loadAndUseClientCargoTemplate.fulfilled, (state, action) => {
        const { data, newDateAdjustment } = action.payload;
        // Convert the client cargo template data to form data
        const x: CargoFormData = {
          ...data.data,
          // Client cargo templates don't carry CMR config.
          cmrConfigs: [],
          clientId: data.data.clientId || undefined,
          customerContactId: data.data.customerContactId || null,
          invoiceRef: data.data.invoiceRef || null,
          description: data.data.description || "",
          description2: data.data.description2 || "",
          qualities: data.data.qualities || [],
          requirements: data.data.requirements || [],
          products: data.data.products,
          clientContactEmail: data.data.clientContactEmail || "",
          clientContactPhone: data.data.clientContactPhone || "",
          clientContact: data.data.clientContact || "",
          driverCanAddDropoffs: data.data.driverCanAddDropoffs || false,
          minTemperature: data.data.minTemperature || "",
          maxTemperature: data.data.maxTemperature || "",
          containerNo: data.data.containerNo || "",
          pickupList: data.data.pickupList.map(apiStopToFormStop),
          dropoffList: data.data.dropoffList.map(apiStopToFormStop),
          stopUnits:
            data.data.stopUnits.map((su) => ({
              ...su,
              ...convertStopUnitsStringToDecimal(su),
            })) || [],
          note: data.data.note || "",
        };

        // Adjust dates based on the new shipment date adjustment setting
        const firstPickupDate = DateTime.fromFormat(
          x.pickupList[0].date,
          "yyyy-MM-dd"
        );
        const diffToToday =
          DateTime.now().startOf("day").diff(firstPickupDate, "days").days +
          Number(newDateAdjustment || 0);
        const dropoffList = x.dropoffList || [];
        const [firstPickupStop, ...restPickups] = x.pickupList;
        firstPickupStop.date = DateTime.now()
          .startOf("day")
          .plus({ days: Number(newDateAdjustment) })
          .toFormat("yyyy-MM-dd");
        firstPickupStop.openingDate = firstPickupStop.openingDate
          ? (() => {
              const dt = DateTime.fromFormat(firstPickupStop.openingDate!, "yyyy-MM-dd").plus({ days: diffToToday });
              return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
            })()
          : null;
        for (const stop of [...restPickups, ...dropoffList]) {
          stop.date = DateTime.fromFormat(stop.date, "yyyy-MM-dd")
            .plus({ days: diffToToday })
            .toFormat("yyyy-MM-dd");
          stop.openingDate = stop.openingDate
            ? (() => {
                const dt = DateTime.fromFormat(stop.openingDate!, "yyyy-MM-dd").plus({ days: diffToToday });
                return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
              })()
            : null;
        }
        const processedData: CargoFormData = {
          ...x,
          dropoffList,
        };

        state.initialFormData = processedData;
        state.resetFormData = processedData;
      });
  },
});

export const { close } = slice.actions;
export default slice.reducer;
